import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core'
import { Apollo } from 'apollo-angular'
import { AuthService } from 'src/app/auth/auth.service'
import { CommService } from 'src/app/core/comm.service'
import { ReevaluationError } from 'src/app/core/errors/errors'
import { reportHandled } from 'src/app/core/errors/issueTracker'
import { TimezoneService } from 'src/app/timezone.service'
import { momentFromDb } from 'src/app/util/util'
import { UserRole } from 'src/types/graphql-global-types'
import { scannersDetails_scanner_by_pk } from '../scanners-details/types/scannersDetails'
import mgBaselineStatusGql from '../trends/mgBaselineStatus.gql'
import {
  mgBaselineStatus,
  mgBaselineStatus_mgBaselineStatus,
  mgBaselineStatus_mgBaselineStatus_tests,
  mgBaselineStatus_mgBaselineStatus_values,
} from '../trends/types/mgBaselineStatus'
import reevaluatePhantomTestsGql from './reevaluatePhantomTests.gql'
import {
  reevaluatePhantomTests,
  reevaluatePhantomTests_reevaluatePhantomTests,
} from './types/reevaluatePhantomTests'

type Status = mgBaselineStatus_mgBaselineStatus
type TestStatus = mgBaselineStatus_mgBaselineStatus_tests
type Outcome = reevaluatePhantomTests_reevaluatePhantomTests

/**
 * "Tests since calibration" on an MG scanner's page: the baseline the next
 * evaluation will use (the server computes it from the current "Last
 * Calibration Date"), and every test since that date with the baseline it
 * was evaluated against. Tests evaluated against a previous baseline can be
 * re-evaluated one by one or all at once; the server does them in parallel
 * and answers per test.
 */
@Component({
  selector: 'app-scanner-baseline',
  templateUrl: './scanner-baseline.component.html',
  styleUrls: ['./scanner-baseline.component.scss'],
})
export class ScannerBaselineComponent implements OnInit, OnChanges {
  @Input() scanner: scannersDetails_scanner_by_pk
  /** the calibration date was just changed on the edit page: say so */
  @Input() calibrationJustChanged = false

  statuses: Status[] = []
  loading = false
  loadError?: string
  /** per test: a re-evaluation is in flight */
  busy: { [testId: number]: boolean } = {}
  /** per test: the outcome of the last re-evaluation in this session */
  outcomes: { [testId: number]: Outcome } = {}
  bulkBusy = false

  /** who may rewrite stored verdicts: same list as the server's REEVALUATE_ROLES */
  reevaluateRoles = [UserRole.admin, UserRole.physicist, UserRole.support]

  constructor(
    private apollo: Apollo,
    private comm: CommService,
    public auth: AuthService,
    private timezone: TimezoneService,
  ) {}

  ngOnInit(): void {
    this.load()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scanner && !changes.scanner.firstChange) {
      this.load()
    }
  }

  get canReevaluate(): boolean {
    return this.auth.checkrole(this.reevaluateRoles)
  }

  load(): void {
    this.loading = true
    this.loadError = undefined
    this.apollo
      .query<mgBaselineStatus>({
        query: mgBaselineStatusGql,
        variables: { scannerId: this.scanner.id, recent: 100 },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: (result) => {
          this.loading = false
          if (result.errors) {
            this.loadError = result.errors[0].message
            reportHandled(result.errors[0], 'load the baseline panel')
            return
          }
          this.statuses = result.data.mgBaselineStatus
        },
        error: (err: unknown) => {
          this.loading = false
          this.loadError =
            err instanceof Error ? err.message : 'could not load the baseline'
          reportHandled(err, 'load the baseline panel')
        },
      })
  }

  formatDate(iso: string): string {
    return momentFromDb(iso).tz(this.timezone.cachedTz).format('MMM DD YYYY')
  }

  formatNumber(value: number | null | undefined, key: string): string {
    if (typeof value !== 'number') {
      return '—'
    }
    const digits: { [key: string]: number } = { sdnr: 4, signal: 1, mAs: 2 }
    return value.toFixed(digits[key] ?? 2)
  }

  valueOf(
    values: mgBaselineStatus_mgBaselineStatus_values[],
    key: string,
  ): mgBaselineStatus_mgBaselineStatus_values | undefined {
    return values.find((v) => v.key === key)
  }

  evaluatedAgainst(test: TestStatus, key: string): number | null {
    const found = test.evaluatedAgainst.find((v) => v.key === key)
    return found ? found.baseline : null
  }

  /** one word per row for the state column */
  stateOf(test: TestStatus): string {
    if (test.status !== 'saved') {
      return 'not completed'
    }
    if (!test.hasSdnr) {
      return 'no SDNR measurement'
    }
    return test.stale ? 'previous baseline' : 'current baseline'
  }

  staleTests(status: Status): TestStatus[] {
    return status.tests.filter((t) => t.stale && t.status === 'saved')
  }

  outcomeOf(test: TestStatus): Outcome | undefined {
    return this.outcomes[test.id]
  }

  async reevaluateOne(status: Status, test: TestStatus): Promise<void> {
    if (
      !window.confirm(
        `Re-evaluate the test of ${this.formatDate(
          test.scanDate,
        )} against the current baseline? Its stored result will be replaced; the technologist's answers are kept.`,
      )
    ) {
      return
    }
    await this.reevaluate(status, [test])
  }

  async reevaluateAll(status: Status): Promise<void> {
    const tests = this.staleTests(status)
    if (tests.length === 0) {
      return
    }
    if (
      !window.confirm(
        `Re-evaluate ${tests.length} test${
          tests.length === 1 ? '' : 's'
        } since the calibration against the current baseline? Their stored results will be replaced; the technologists' answers are kept.`,
      )
    ) {
      return
    }
    await this.reevaluate(status, tests)
  }

  private async reevaluate(status: Status, tests: TestStatus[]): Promise<void> {
    const ids = tests.map((t) => t.id)
    ids.forEach((id) => {
      this.busy[id] = true
    })
    this.bulkBusy = true
    let outcomes: Outcome[]
    try {
      const result = await this.apollo
        .mutate<reevaluatePhantomTests>({
          mutation: reevaluatePhantomTestsGql,
          variables: { phantomTestIds: ids },
        })
        .toPromise()
      if (result.errors) {
        throw result.errors[0]
      }
      outcomes = result.data.reevaluatePhantomTests
    } finally {
      ids.forEach((id) => {
        this.busy[id] = false
      })
      this.bulkBusy = false
    }
    outcomes.forEach((outcome) => {
      this.outcomes[outcome.id] = outcome
    })
    const failures = outcomes.filter((o) => !!o.error)
    const done = outcomes.length - failures.length
    if (done > 0) {
      this.comm.alert(
        'success',
        `${done} test${done === 1 ? '' : 's'} re-evaluated (${status.routineName})`,
      )
    }
    this.load()
    if (failures.length > 0) {
      throw new ReevaluationError(
        `${failures.length} of ${outcomes.length} tests could not be re-evaluated: ${failures
          .map((f) => `#${f.id}: ${f.error}`)
          .join('; ')}`,
        failures.map((f) => ({ id: f.id, error: f.error || 'unknown' })),
      )
    }
  }
}
